import React from "react";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import {
  AdminPanelSettings,
  Delete,
  Edit,
  Visibility,
} from "@mui/icons-material";
import theme from "../../theme";
import Button from "./Button";

interface MobileDataGridProps<T> {
  rows: T[];
  headers: string[];
  handleEdit?: (id: number) => void;
  handleDelete?: (id: number) => void;
  handleAdminRoute?: (id: number) => void;
  handleView?: (id: number) => void;
}

const MobileTableStack: React.FC<MobileDataGridProps<any>> = ({
  rows,
  headers,
  handleEdit,
  handleDelete,
  handleAdminRoute,
  handleView,
}) => {
  if (!rows || rows.length === 0) return null;

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{ alignItems: "center", width: "100%", minWidth: 0 }}
      useFlexGap
    >
      {rows.map((row, index) => {
        const { id, ...restOfRow } = row;
        const hasButtons =
          !!id && (!!handleDelete || !!handleEdit || !!handleAdminRoute);

        const pairs = [];
        for (let i = 0; i < headers.length; i += 2) {
          pairs.push(headers.slice(i, i + 2));
        }

        return (
          <Grid2
            key={index}
            container
            width="100%"
            padding={1}
            sx={{
              backgroundColor:
                index % 2 === 0
                  ? theme.palette.background.default
                  : theme.palette.action.hover,
              borderRadius: 1,
              boxShadow: 1,
              marginBottom: 2,
              overflow: "hidden",
            }}
          >
            {hasButtons && (
              <Grid2
                // direction="column"
                size={2}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                {!!handleView && (
                  <Box display="flex" flexDirection="row">
                    <Button
                      onClick={() => handleView(Number(id))}
                      color="success"
                      icon={
                        <Visibility
                          fontSize={"small"}
                          aria-label="view-button"
                        />
                      }
                    />
                  </Box>
                )}
                {!!handleEdit && (
                  <Box display="flex" flexDirection="row">
                    <Button
                      onClick={() => handleEdit(Number(id))}
                      color="primary"
                      icon={
                        <Edit fontSize={"medium"} aria-label="edit-button" />
                      }
                    />
                  </Box>
                )}
                {!!handleDelete && (
                  <Box display="flex" flexDirection="row">
                    <Button
                      onClick={() => handleDelete(Number(id))}
                      color="error"
                      icon={
                        <Delete
                          fontSize={"medium"}
                          aria-label="delete-button"
                        />
                      }
                    />
                  </Box>
                )}
                {!!handleAdminRoute && (
                  <Box display="flex" flexDirection="row">
                    <Button
                      onClick={() => handleAdminRoute(Number(id))}
                      color="secondary"
                      icon={
                        <AdminPanelSettings
                          fontSize={"medium"}
                          aria-label="admin-button"
                        />
                      }
                    />
                  </Box>
                )}
              </Grid2>
            )}
            <Grid2
              // direction="column"
              size={hasButtons ? 10 : 12}
              textAlign="center"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box paddingX={1} width="100%">
                {pairs.map((pair, rowIndex) => (
                  <Box
                    key={rowIndex}
                    display="flex"
                    flexDirection="row"
                    justifyContent={
                      pair.length === 2 ? "space-between" : "flex-start"
                    }
                    flexWrap="nowrap"
                    gap={2}
                    marginBottom={1}
                    width="100%"
                  >
                    {pair.map((h, colIndex) => (
                      <Box
                        key={colIndex}
                        display="flex"
                        flexDirection="column"
                        textAlign={colIndex === 0 ? "left" : "right"}
                        minWidth={0}
                        sx={{ minWidth: "45%", maxWidth: "50%" }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          textTransform="capitalize"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {h}:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-word", maxWidth: "100%" }}
                        >
                          {restOfRow[h]}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Grid2>
          </Grid2>
        );
      })}
    </Stack>
  );
};

export default MobileTableStack;
